/**
 * useTodos - Real-time subscription to the current user's todo items
 *
 * Splits todos into active (incomplete) and completed lists.
 * Completed list limited to last 20 items.
 */

import { useState, useEffect, useMemo } from 'react';
import { subscribeTodos } from '@/services/todo.service';
import type { TodoItem } from '@/types/schema';

interface UseTodosReturn {
  todos: TodoItem[];
  completedTodos: TodoItem[];
  loading: boolean;
}

export function useTodos(userId: string, tenantId: string): UseTodosReturn {
  const [allTodos, setAllTodos] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !tenantId) {
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeTodos(userId, tenantId, (items) => {
      setAllTodos(items);
      setLoading(false);
    });

    return unsubscribe;
  }, [userId, tenantId]);

  const { todos, completedTodos } = useMemo(() => {
    const active = allTodos
      .filter((t) => !t.completed)
      .sort((a, b) => a.order - b.order);

    const completed = allTodos
      .filter((t) => t.completed)
      .sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''))
      .slice(0, 20);

    return { todos: active, completedTodos: completed };
  }, [allTodos]);

  return { todos, completedTodos, loading };
}
